import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import InlineTextEditCallback from '../models/InlineTextEditCallback';
import Modaliser from './Modaliser';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface InlineTextEditProps<T> {
  label?: string;
  value: string;
  children: JSX.Element;
  errorMessage: string;
  onSubmit: (value: string, cb: InlineTextEditCallback<T>) => void;
}

function InlineTextEdit<T>(props: InlineTextEditProps<T>) {
  const initialValue = props.value;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [changed, setChanged] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (editing) {
      setValue(initialValue);
      setUpdating(false);
      setError(false);
    }
  }, [editing]);

  function canSubmit() {
    return (
      !updating && changed && value?.trim() && value.trim() !== initialValue
    );
  }

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    setEditing(true);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
    if (!changed) {
      setChanged(true);
    }
  }

  function handleEvents<T>(loading: boolean, error: boolean, data: T) {
    if (loading) {
      return;
    }
    if (error) {
      setError(true);
    }
    if (data) {
      setEditing(false);
    }
    setUpdating(false);
  }

  function handleOk(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setUpdating(true);
    props.onSubmit(value.trim(), handleEvents);
  }

  function handleCancel(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setEditing(false);
  }

  return (
    <div onClick={handleClick}>
      {editing ? (
        <Modaliser>
          <Grid container alignItems="center">
            <Grid>
              <TextField
                variant="outlined"
                label={props.label}
                value={value}
                size="small"
                autoFocus
                disabled={updating}
                error={error}
                helperText={error ? props.errorMessage : ''}
                onChange={handleChange}
              />
            </Grid>
            {updating && <CustomLoadingProgress size={20} />}
            <Grid>
              <IconButton
                size="small"
                disabled={!canSubmit()}
                onClick={handleOk}
              >
                <CheckIcon />
              </IconButton>
            </Grid>
            <Grid>
              <IconButton size="small" onClick={handleCancel}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Modaliser>
      ) : (
        <Tooltip title="Click to edit" arrow placement="left" enterDelay={200}>
          {props.children}
        </Tooltip>
      )}
    </div>
  );
}

export default InlineTextEdit;
